import { type Dispatch, type SetStateAction, useRef } from 'react';
import { ACCEPTED_TYPES, uploadPendingFiles, type PendingAttachment } from './chat-attachment-upload.js';
import './ChatAttachmentPickerComponent.css';

export type { PendingAttachment } from './chat-attachment-upload.js';

/**
 * Adds files via the paperclip button. What is already staged is rendered by
 * ChatAttachmentTrayComponent, on its own row above the composer — see the note
 * there for why they are not together.
 *
 * The actual staging/upload logic lives in chat-attachment-upload.ts so the
 * welcome and active-thread composer drop zones can share it.
 */
interface ChatAttachmentPickerProps {
  attachments: PendingAttachment[];
  onAttachmentsChange: Dispatch<SetStateAction<PendingAttachment[]>>;
  disabled?: boolean;
}

export function ChatAttachmentPickerComponent({ attachments, onAttachmentsChange, disabled }: ChatAttachmentPickerProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handlePickerClick(): void {
    fileInputRef.current?.click();
  }

  async function handleFiles(files: FileList | null): Promise<void> {
    await uploadPendingFiles(files, attachments, onAttachmentsChange);
    // Reset input so the same file can be re-added
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div className="attachment-picker">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        className="attachment-picker__input"
        onChange={(e) => void handleFiles(e.target.files)}
        disabled={disabled}
      />
      <button
        type="button"
        className="attachment-picker__btn"
        onClick={handlePickerClick}
        disabled={disabled}
        title="Attach files (images, PDF, Word, Excel, PowerPoint, text, data and code files)"
        aria-label="Attach files"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
        </svg>
      </button>
    </div>
  );
}
